import * as io from 'io-ts';
import { fromRecord } from '../locale';
import { nullable } from '../source/io/io';
import { MessageRecordIO, MessageRecordLangIO } from '../source/io/message';

export const ServiceDescriptionIO = io.interface({
    code: io.string,
    name: MessageRecordIO,
    placeholder: nullable(MessageRecordIO),
});
export type ServiceDescription = io.TypeOf<typeof ServiceDescriptionIO>;

const TermRequestIO = io.interface(
    {
        service: io.string,
        input: io.string,
        lang: MessageRecordLangIO,
    },
    'TermRequestIO',
);

const LayerParameterIO = io.intersection([
    io.interface({ layerId: io.string }),
    io.partial({ fields: io.array(io.string) }),
]);
export type LayerParameter = io.TypeOf<typeof LayerParameterIO>;

export const LayerRequestIO = io.intersection(
    [
        TermRequestIO,
        io.interface({
            layers: io.array(LayerParameterIO),
        }),
    ],
    'LayerRequestIO',
);

export const ServiceRequestIO = io.union([TermRequestIO, LayerRequestIO]);
export type ServiceRequest = io.TypeOf<typeof ServiceRequestIO>;

export const ServiceResultMessageIO = io.union([MessageRecordIO, io.string]);
export type ServiceResultMessage = io.TypeOf<typeof ServiceResultMessageIO>;

const TermResultIO = io.interface(
    {
        coord: io.interface({ y: io.number, x: io.number }),
        message: ServiceResultMessageIO,
    },
    'TermResultIO',
);
export type TermResult = io.TypeOf<typeof TermResultIO>;

const LayerResultIO = io.intersection(
    [
        TermResultIO,
        io.interface({
            layerId: io.string,
            field: io.string,
            featureId: io.union([io.number, io.string]),
        }),
    ],
    'LayerResultIO',
);
export type LayerResult = io.TypeOf<typeof LayerResultIO>;

export const ServiceResultIO = io.union([LayerResultIO, TermResultIO]);
export type ServiceResult = io.TypeOf<typeof ServiceResultIO>;

export const isLayerResult = (result: ServiceResult): result is LayerResult =>
    'layerId' in result;

export const areLayerResults = (
    results: ServiceResult[],
): results is LayerResult[] => results.length > 0 && isLayerResult(results[0]);

export const ServiceResponseIO = io.interface({
    results: io.union([io.array(LayerResultIO), io.array(TermResultIO)]),
    error: nullable(io.string),
});
export type ServiceResponse = io.TypeOf<typeof ServiceResponseIO>;

export const serviceMessageToString = (msg: ServiceResultMessage) =>
    typeof msg === 'string' ? msg : fromRecord(msg);
